import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Skeleton, EmptyState, BADGE_TONE_COLOR } from '../../../../components';
import type { OperationRow } from '../../../../mock-data/types';
import { dateLocale } from '../../../../utils/locale';
import {
  FeedPanel,
  FeedHead,
  LiveTitle,
  LiveDot,
  HeaderRow,
  HeaderMain,
  HeaderCell,
  HeaderStatus,
  FeedList,
  Row,
  RowMain,
  OpName,
  Cell,
  TimeCell,
  MobileTime,
  MobileMeta,
  MetaLine,
  StatusCell,
  StatusText,
  SkeletonRow,
  SkeletonStatus,
} from './OperationsFeed.styles';

/** Skeleton placeholder rows shown while the first page loads. */
const SKELETON_ROWS = 5;

/** How the Time column reads: "Aug 04, 14:00" in the reader's own timezone. */
const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

/**
 * When the operation landed, or is due to land (the API timestamps are UTC; the
 * formatter renders them locally). An absent or unparseable timestamp leaves the
 * cell blank rather than printing an epoch or "Invalid Date".
 */
const formatEventTime = (iso: string | undefined, format: Intl.DateTimeFormat): string => {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  return format.format(ms);
};

/**
 * Localise a closed backend vocabulary at the render site. `Object.hasOwn` rather
 * than a plain lookup so a value naming an `Object.prototype` member (`__proto__`,
 * `constructor`) MISSES the map and falls back to itself, instead of resolving to
 * a prototype member and rendering as "[object Object]".
 */
const labelFor = (labels: Record<string, string>, value: string): string =>
  Object.hasOwn(labels, value) ? labels[value] : value;

/**
 * Where the feed parks itself on arrival: the newest operation that has already
 * run. Rows come newest-first, so that is the first `executed` one, and centring
 * it puts everything still scheduled above the fold and everything past below.
 * `null` (a stream that is entirely scheduled) means there is nothing to centre
 * on and the feed simply opens at the top. Identified by ID, not by position:
 * the feed re-sorts as operations run, so a positional anchor would follow
 * whatever row happens to land at that index.
 */
const latestExecutedId = (rows: OperationRow[]): string | null =>
  rows.find((row) => row.status.phase === 'executed')?.id ?? null;

/** Fade an edge only once there is genuinely more feed hidden past it. */
type FadeState = { top: boolean; bottom: boolean };

/**
 * The row the reader is currently reading from: the topmost one still inside the
 * scroll viewport, identified by `id` (NOT by position). Holding the position
 * steady across polls has to key on identity — the feed re-sorts as operations
 * run, so "the row that was at index N" can be a different operation entirely.
 */
type ReadingAnchor = { id: string; top: number };

const readingAnchor = (list: HTMLElement): ReadingAnchor | null => {
  const { scrollTop } = list;
  for (const child of Array.from(list.children) as HTMLElement[]) {
    const id = child.dataset.rowId;
    if (id != null && child.offsetTop + child.offsetHeight > scrollTop) return { id, top: child.offsetTop };
  }
  return null;
};

/** Where a given row sits now, or `null` if this batch no longer carries it. */
const rowTop = (list: HTMLElement, id: string): number | null => {
  for (const child of Array.from(list.children) as HTMLElement[]) {
    if (child.dataset.rowId === id) return child.offsetTop;
  }
  return null;
};

/**
 * Track which rows are "fresh" (newly arrived since the previous poll). The first
 * batch after a (re)load (initial mount or a brand switch) is the baseline and
 * does NOT flash; only rows the live poll adds on top of it flash green.
 */
const useFreshIds = (rows: OperationRow[], loading: boolean): ReadonlySet<string> => {
  const [freshIds, setFreshIds] = React.useState<ReadonlySet<string>>(() => new Set<string>());
  const seenIdsRef = React.useRef<Set<string>>(new Set());
  const baselineSetRef = React.useRef(false);

  React.useEffect(() => {
    // A new load cycle re-baselines: the next arriving batch is the seed (no flash).
    if (loading) {
      baselineSetRef.current = false;
      seenIdsRef.current = new Set();
      setFreshIds(new Set());
      return;
    }
    const ids = rows.map((row) => row.id);
    if (!baselineSetRef.current) {
      baselineSetRef.current = true;
      setFreshIds(new Set());
    } else {
      setFreshIds(new Set(ids.filter((id) => !seenIdsRef.current.has(id))));
    }
    seenIdsRef.current = new Set(ids);
  }, [rows, loading]);

  return freshIds;
};

export type OperationsFeedProps = {
  /** Live-feed rows (newest first), sourced from CRM Events `/live-events`. */
  rows: OperationRow[];
  /** First-page load in flight: shows skeleton rows. */
  loading: boolean;
};

/**
 * Live Operations feed: the Operation / Category / Scope / Channel / Time / Status
 * table sourced from the CRM Events `/live-events` endpoint (polled for the active
 * brand). A full page of the tenant's stream is fetched and rendered as ONE
 * continuous timeline inside a scrolling panel, parked on the newest operation
 * that has already run: scroll up for what is still scheduled, down for what
 * already happened. Rows the poll adds on top slide in with a brief green
 * highlight; a pulsing dot beside the panel's "Live" title marks the stream.
 * Reflows from a table (desktop) to stacked cards (mobile).
 */
export const OperationsFeed = ({ rows, loading }: OperationsFeedProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const freshIds = useFreshIds(rows, loading);

  const listRef = React.useRef<HTMLDivElement>(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  // One auto-scroll per load cycle: a poll must never yank the feed back from
  // wherever the owner scrolled it to.
  const parkedRef = React.useRef(false);
  // The row the reader was on, and where it sat, as of the last time the feed's
  // geometry was known good. A poll that inserts operations above it is undone
  // by putting that same row back where it was (see the layout effect).
  const readingRef = React.useRef<ReadingAnchor | null>(null);
  const [fade, setFade] = React.useState<FadeState>({ top: false, bottom: false });

  const anchorId = latestExecutedId(rows);

  // The app's language is a locale FOLDER name, not a BCP-47 tag, so it goes
  // through `dateLocale` — handing `Intl` a bare `mx` silently falls back to the
  // browser's locale and prints an English month inside Spanish copy. One
  // formatter per language, reused by every row (a `toLocaleString` per cell
  // builds and throws away one on each call, 200 of them per render here).
  const locale = dateLocale(i18n.language);
  const timeFormat = React.useMemo(() => new Intl.DateTimeFormat(locale, TIME_FORMAT), [locale]);

  // Syncs the edge masks with the scroll position (external DOM state: the
  // container's own scroll metrics, which React does not observe).
  const updateFade = React.useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const scrollable = scrollHeight - clientHeight > 1;
    const top = scrollable && scrollTop > 1;
    const bottom = scrollable && scrollTop < scrollHeight - clientHeight - 1;
    setFade((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);

  // Owns the feed's scroll position. A layout effect, not a passive one: it reads
  // layout (`offsetTop`/`clientHeight`) and writes a scroll offset, so running it
  // before paint is what stops the feed flashing at the top and snapping down on
  // every load.
  React.useLayoutEffect(() => {
    const list = listRef.current;
    // A reload (mount or brand switch), and an emptied feed, both re-baseline:
    // there is no reading position to hold, and the next batch parks afresh. The
    // masks are recomputed too, or the short skeleton block inherits the fades of
    // the longer stream it replaced.
    if (loading || rows.length === 0) {
      parkedRef.current = false;
      readingRef.current = null;
      updateFade();
      return;
    }
    if (!list) return;

    if (!parkedRef.current) {
      // Opening position: centre the newest already-run operation. Only latch
      // once that row actually exists, so an all-scheduled first page parks later
      // instead of never. `offsetTop`/`offsetHeight` are layout values, so the
      // rows' slide-in transform cannot skew the landing spot; the browser clamps
      // when the anchor sits within half a viewport of either end.
      const anchor = anchorRef.current;
      if (anchor) {
        parkedRef.current = true;
        list.scrollTop = anchor.offsetTop - (list.clientHeight - anchor.offsetHeight) / 2;
      }
    } else {
      // A poll rewrote the list. Operations are inserted above the reader as they
      // arrive, so put the row they were reading back where it was. This tracks
      // the row by ID on purpose: the feed re-sorts as operations run, so a
      // positional anchor would "move" whenever a scheduled row flipped to
      // executed and would scroll the feed for a change that shifted nothing.
      // The list's own height cannot measure this either, since a fixed page size
      // drops as many rows off the bottom as it gains on top.
      const was = readingRef.current;
      if (was != null && list.scrollTop > 0) {
        const now = rowTop(list, was.id);
        if (now != null && now !== was.top) list.scrollTop += now - was.top;
      }
    }
    readingRef.current = readingAnchor(list);
    updateFade();
  }, [rows, loading, updateFade]);

  // The masks are a function of the container's geometry, which changes without a
  // scroll or a new batch: a viewport resize, or crossing the mobile breakpoint
  // (where the feed's max-height changes).
  React.useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      // A resize moves every row, so the stored reading position is measured
      // against a layout that no longer exists: re-baseline it here, or the next
      // poll would "correct" for a shift the reader already saw happen.
      readingRef.current = readingAnchor(list);
      updateFade();
    });
    observer.observe(list);
    return () => observer.disconnect();
  }, [updateFade]);

  // The operation NAME is API data (left raw); the category / scope / channel /
  // status are fixed closed vocabularies driving the feed columns. They reach us
  // as plain strings, so localise the full union of their values here at the
  // render site. A value outside the map falls back to itself.
  const CATEGORY_LABELS: Record<string, string> = {
    Monetization: t('Monetization'),
    Retention: t('Retention'),
    Conversion: t('Conversion'),
    Acquisition: t('Acquisition'),
  };
  const SCOPE_LABELS: Record<string, string> = {
    'Churn prevention': t('Churn prevention'),
    LTV: t('LTV'),
    Reactivation: t('Reactivation'),
    'Cross-sell': t('Cross-sell'),
    Onboarding: t('Onboarding'),
    'Win-back': t('Win-back'),
    'Multiple audiences': t('Multiple audiences'),
    'Non-depositor': t('Non-depositor'),
  };
  const CHANNEL_LABELS: Record<string, string> = {
    Push: t('Push'),
    SMS: t('SMS'),
    Mail: t('Mail'),
    'In-app': t('In-app'),
    WhatsApp: t('WhatsApp'),
  };
  const STATUS_LABELS: Record<string, string> = {
    Scheduled: t('Scheduled'),
    Executed: t('Executed'),
    Executing: t('Executing'),
    Queued: t('Queued'),
    'Not sent': t('Not sent'),
    Failed: t('Failed'),
  };

  return (
    <FeedPanel>
      <FeedHead>
        <LiveTitle text="Live" tag="h3" />
        <LiveDot />
      </FeedHead>

      <HeaderRow>
        <HeaderMain>
          <HeaderCell text="Operation" tag="span" />
          <HeaderCell text="Category" tag="span" />
          <HeaderCell text="Scope" tag="span" />
          <HeaderCell text="Channel" tag="span" />
          <HeaderCell text="Time" tag="span" />
        </HeaderMain>
        <HeaderStatus text="Status" tag="span" />
      </HeaderRow>

      <FeedList
        data-testid="operations-feed-list"
        ref={listRef}
        onScroll={updateFade}
        $fadeTop={fade.top}
        $fadeBottom={fade.bottom}
        // The rows hold nothing focusable, so without this the capped stream is
        // unreachable for a keyboard-only reader on browsers that don't focus
        // scroll regions by themselves.
        tabIndex={0}
        role="region"
        aria-label={t('Live operations feed')}
      >
        {loading ? (
          Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <SkeletonRow key={`skeleton-${index}`}>
              <RowMain>
                <Skeleton width="70%" height={15} />
                <Skeleton width="60%" height={13} />
                <Skeleton width="55%" height={13} />
                <Skeleton width="50%" height={13} />
                <Skeleton width="65%" height={13} />
              </RowMain>
              <SkeletonStatus>
                <Skeleton width={72} height={14} />
              </SkeletonStatus>
            </SkeletonRow>
          ))
        ) : rows.length === 0 ? (
          <EmptyState text={t('No live operations yet')} />
        ) : (
          // Every row is a FLAT child of the scroller: the parking and
          // hold-your-place effects above measure rows by `offsetTop` against this
          // container, and any wrapper would put another offset parent between them.
          rows.map((row) => {
            const category = labelFor(CATEGORY_LABELS, row.category);
            const scope = labelFor(SCOPE_LABELS, row.scope);
            const channel = labelFor(CHANNEL_LABELS, row.channel);
            const time = formatEventTime(row.at, timeFormat);
            return (
              <Row
                key={row.id}
                data-row-id={row.id}
                ref={row.id === anchorId ? anchorRef : undefined}
                $fresh={freshIds.has(row.id)}
              >
                <RowMain>
                  <OpName text={row.name} tag="span" />
                  <Cell text={category} tag="span" />
                  <Cell text={scope} tag="span" />
                  <Cell text={channel} tag="span" />
                  <TimeCell text={time} tag="span" />
                  <MobileMeta>
                    <MobileTime text={time} tag="span" />
                    <MetaLine text="Category: {{value}}" placeholders={{ value: category }} tag="span" />
                    <MetaLine text="Scope: {{value}}" placeholders={{ value: scope }} tag="span" />
                    <MetaLine text="Channel: {{value}}" placeholders={{ value: channel }} tag="span" />
                  </MobileMeta>
                </RowMain>
                <StatusCell>
                  <Icon name={row.status.icon} size={15} color={BADGE_TONE_COLOR[row.status.tone]} />
                  <StatusText
                    text={labelFor(STATUS_LABELS, row.status.label)}
                    $color={BADGE_TONE_COLOR[row.status.tone]}
                    tag="span"
                  />
                </StatusCell>
              </Row>
            );
          })
        )}
      </FeedList>
    </FeedPanel>
  );
};

export default OperationsFeed;
